import classNames from 'classnames';
import {
  type ButtonHTMLAttributes,
  type FunctionComponent,
  type MouseEventHandler,
  type ReactNode,
} from 'react';
import './button.css';
import { rootClassName, variants } from './Button.helpers';
import { themes } from './Button.themes';
import { ButtonContent } from './ButtonContent';
import type { IconicNames, IconicTypes } from '@/atoms/Icons/index.js';

export interface CommonButtonProps {
  /**
   * Button themes defining background color and text/icon color
   */
  theme?: 'yellow' | 'white' | 'black' | 'whiteStroke' | 'blackStroke';
  /**
   * Is it a text button? An icon button? An arrow button?
   */
  variant?: 'text' | 'textSmall' | 'icon' | 'arrow';
  /**
   * Background Color override
   * Ideally please use ONLY for the "white" and "black" themes
   */
  backgroundOverride?: string;
  /**
   * Additional class names
   */
  className?: string;
  /**
   * Button title
   */
  title?: string;
  /**
   * Button contents
   */
  label?: string;
  /**
   * Optional children (you might never use this, please actually try to avoid it)
   */
  children?: ReactNode;
  /**
   * Optional icon name
   */
  icon?: IconicNames;
  /**
   * Force the icon type to be svg, font or default
   */
  iconType?: IconicTypes;
  /**
   * Data test id
   */
  dataTestId?: string;
  /**
   * Group name
   * Used to group buttons together (you might not need this)
   */
  groupName?: string;
}

interface ButtonProps extends CommonButtonProps, ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * Optional click handler
   */
  onClick?: MouseEventHandler<HTMLButtonElement>;
  /**
   * Button tabIndex
   *
   * @default 0
   */
  tabIndex?: number;
  /**
   * Data test id
   */
  dataTestId?: string;
}

export const Button: FunctionComponent<ButtonProps> = ({
  type = 'button',
  theme: themeName = 'yellow',
  backgroundOverride,
  variant = 'text',
  className,
  label,
  children,
  title,
  icon,
  iconType,
  tabIndex = 0,
  disabled = false,
  dataTestId,
  groupName = '',
  ...rest
}) => {
  const showLabel = variant === 'text' || variant === 'textSmall';
  const layout = variants[variant];
  const theme = themes(themeName, backgroundOverride, groupName);

  return (
    <button
      {...rest}
      data-name="Button"
      data-testid={dataTestId}
      type={type}
      className={classNames(rootClassName, theme, layout, variant, className, {
        'pointer-events-none': disabled,
      })}
      disabled={disabled}
      tabIndex={disabled ? -1 : tabIndex}
      title={title || label}
      aria-label={title || label}
    >
      <ButtonContent showLabel={showLabel} label={label} icon={icon} iconType={iconType}>
        {children}
      </ButtonContent>
    </button>
  );
};
